import { ComponentType, lazy, Suspense } from "react";

import { isStaleChunkError, reloadForStaleChunkWithCooldown } from "@/lib/stale-chunk";

type StaleChunkRecoveryOptions = {
  isProd?: boolean;
  reload?: () => void;
};

export function loadWithStaleChunkRecovery<T>(
  factory: () => Promise<T>,
  { isProd = process.env.NODE_ENV === "production", reload }: StaleChunkRecoveryOptions = {},
): Promise<T> {
  return factory().catch((error: unknown) => {
    if (isProd && isStaleChunkError(error) && reloadForStaleChunkWithCooldown(reload)) {
      return new Promise<T>(() => {});
    }
    throw error;
  });
}

// Must be called at module scope: React.lazy requires a stable component identity
// across renders, and calling lazyRoute inside a component would mint a new one
// every render.
export function lazyRoute<M extends Record<K, ComponentType>, K extends string>(
  factory: () => Promise<M>,
  exportName: K,
): ComponentType {
  const LazyComponent = lazy(async () => {
    const module = await loadWithStaleChunkRecovery(factory);
    return { default: module[exportName] as ComponentType };
  });

  return function LazyRouteComponent() {
    return (
      <Suspense fallback={<></>}>
        <LazyComponent />
      </Suspense>
    );
  };
}
